import { useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Label } from "@fronterahealth/frontera-ui-components";

import { FormInputField } from "@components/AssessmentFormRenderer/components/FormInputField";
import { GroupedRowsSection } from "@components/AssessmentFormRenderer/types/layoutTypes";
import { getNestedError } from "@components/AssessmentFormRenderer/utils/getNestedError";

interface FormGroupedRowsProps {
  section: GroupedRowsSection;
}

interface Group {
  label: string;
  subGroups: { label: string; indices: number[] }[];
}

/** Parse "code_score" → ["code", "score"], "value" → ["value"] */
function parseRowLayoutKeys(rowLayout: string): string[] {
  return rowLayout.split("_").filter(Boolean);
}

export const FormGroupedRows: React.FC<FormGroupedRowsProps> = ({ section }) => {
  const { control, getValues, formState } = useFormContext();
  const [groupKey, subGroupKey] = section.groupBy;

  const { fields } = useFieldArray({
    control,
    name: section.source,
  });

  const values = getValues(section.source) as Record<string, unknown>[] | undefined;

  const groups = useMemo(() => {
    if (!values) return [] as Group[];

    const groupMap = new Map<string, Map<string, number[]>>();

    values.forEach((row, index) => {
      const group = String(row[groupKey] ?? "");
      const subGroup = String(row[subGroupKey] ?? "");

      if (!groupMap.has(group)) {
        groupMap.set(group, new Map());
      }
      const subs = groupMap.get(group)!;
      if (!subs.has(subGroup)) {
        subs.set(subGroup, []);
      }
      subs.get(subGroup)!.push(index);
    });

    return Array.from(groupMap.entries()).map(([label, children]) => ({
      label,
      subGroups: Array.from(children.entries()).map(([subLabel, indices]) => ({
        label: subLabel,
        indices,
      })),
    }));
  }, [fields, values, groupKey, subGroupKey]);

  return (
    <div className="flex flex-col gap-8">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-4">
          <Label displayType="loud" className="text-base">
            {group.label}
          </Label>
          {group.subGroups.map((sub) => (
            <div key={sub.label} className="flex flex-col gap-1 ml-2">
              <Label displayType="loud">{sub.label}</Label>
              {sub.indices.map((rowIndex) => {
                const rowLayout = String(values?.[rowIndex]?.[section.rowLayoutKey] ?? "");
                const keys = parseRowLayoutKeys(rowLayout);

                return (
                  <div key={fields[rowIndex]?.id ?? rowIndex} className="flex flex-row items-center gap-x-4">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm">{getValues(`${section.source}.${rowIndex}.${section.nameKey}`)}</span>
                    </div>
                    {keys.map((key) => {
                      const col = section.columns[key];
                      if (!col) return null;
                      const fieldFullPath = `${section.source}.${rowIndex}.${key}`;
                      const error = getNestedError(formState.errors, fieldFullPath);
                      return (
                        <div key={key} className="w-28 flex-none">
                          <FormInputField
                            fieldPath={fieldFullPath}
                            inputType={col.validation?.inputType ?? "text"}
                            editable={col.editable}
                            error={error}
                            validation={col.validation}
                            label={col.label}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
